import { useState, useRef } from 'react';
import { FileText, Check, CheckCircle2 } from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/api/client';
import { toast } from '@/state/toast';
import { track } from '@/state/telemetry';

interface Props {
  projectId: string;
  onClose: () => void;
}

type Step = 1 | 2 | 3;

interface ResponsePreview {
  num: string;
  section: string;
  text: string;
}

/** Import responses from Word file (.docx). Для роли GIP — массовая загрузка ответов на замечания. */
export function DocxResponseImportModal({ projectId, onClose }: Props) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>(1);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ResponsePreview[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const fileRef = useRef<HTMLInputElement>(null);

  const importMut = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Файл не выбран');
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`/projects/${projectId}/import/responses-docx`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) {
        let detail: unknown;
        try { detail = await res.json(); } catch { /* swallow */ }
        throw new ApiError(res.status, `HTTP ${res.status}`, detail);
      }
      return res.json() as Promise<{ imported: number }>;
    },
    onSuccess: (data) => {
      toast.success(`Импортировано ${data.imported} ответов`);
      track('responses_imported_docx', { project_id: projectId, payload: { count: data.imported } });
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
      setStep(3);
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Не удалось импортировать'),
  });

  const onFile = (f: File) => {
    setFile(f);
    // Mock preview (backend сам разбирает на серверной стороне; Sprint 5+: добавить /preview эндпоинт)
    const mock: ResponsePreview[] = [
      { num: '#9', section: 'ТКР', text: 'Расчёт уклона приложен согласно СП 37.13330.2012 п.4.2.7' },
      { num: '#6', section: 'ТКР', text: 'Расчётное обоснование радиуса R=155м приложено к ТКР лист 12' },
      { num: '#3', section: 'ООС', text: 'Класс водного объекта добавлен согласно приказу Росрыболовства №20' },
      { num: '#4', section: 'ПОС', text: 'Технологические карты разработаны согласно МДС 12-81.2007' },
    ];
    setPreview(mock);
    setSelected(new Set(mock.map((m) => m.num)));
    setStep(2);
  };

  const toggle = (num: string) => {
    const next = new Set(selected);
    next.has(num) ? next.delete(num) : next.add(num);
    setSelected(next);
  };

  let footer: React.ReactNode = null;
  if (step === 1) {
    footer = (
      <>
        <Button variant="ghost" onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled>Далее</Button>
      </>
    );
  } else if (step === 2) {
    footer = (
      <>
        <Button variant="ghost" onClick={() => setStep(1)}>← Назад</Button>
        <Button
          variant="primary"
          onClick={() => importMut.mutate()}
          disabled={selected.size === 0 || importMut.isPending}
        >{importMut.isPending ? 'Импорт…' : `Импортировать ${selected.size} ${plural(selected.size, 'ответ', 'ответа', 'ответов')}`}</Button>
      </>
    );
  } else {
    footer = <Button variant="primary" onClick={onClose} style={{ width: '100%' }}>Закрыть</Button>;
  }

  return (
    <Modal title="Импорт ответов из Word" onClose={onClose} maxWidth={620} footer={footer}>
      <StepIndicator current={step} />

      {step === 1 && (
        <div className="mt-6">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-card text-center"
            style={{ padding: 32, background: '#F3F4F6', border: '2px dashed #E5E7EB', cursor: 'pointer' }}
          >
            <FileText size={40} stroke="#6B7280" style={{ margin: '0 auto' }} />
            <div className="text-ink mt-3" style={{ fontSize: 15, fontWeight: 500 }}>Перетащите .docx с ответами ГИП или нажмите для выбора</div>
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>Поддерживается формат «Реестр ответов ГИП»</div>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".docx"
            onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); }}
            style={{ display: 'none' }}
          />
          <p className="text-muted mt-3" style={{ fontSize: 12 }}>
            Формат: каждый ответ — пункт списка с номером замечания (#N) и текстом ответа.
            Подробности — в шаблоне документации.
          </p>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6">
          <div className="rounded-card" style={{ padding: 16, border: '1px solid #E5E7EB' }}>
            <div className="flex items-center justify-between">
              <div className="font-heading text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
                Распознано {preview.length} {plural(preview.length, 'ответ', 'ответа', 'ответов')} из {preview.length}
              </div>
              <span className="text-muted tabular" style={{ fontSize: 11 }}>{file?.name} · {((file?.size || 0) / 1024).toFixed(0)} КБ</span>
            </div>
            <div className="mt-3">
              {preview.map((r) => (
                <label
                  key={r.num}
                  className="flex items-start cursor-pointer"
                  style={{ padding: '10px 0', borderBottom: '1px solid #E5E7EB', gap: 10 }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(r.num)}
                    onChange={() => toggle(r.num)}
                    style={{ marginTop: 4, accentColor: '#F59E0B' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div className="text-muted" style={{ fontSize: 11 }}>{r.num} · {r.section}</div>
                    <div className="text-ink mt-0.5" style={{ fontSize: 13 }}>{r.text}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="text-center" style={{ padding: '32px 0' }}>
          <CheckCircle2 size={56} stroke="#10B981" style={{ margin: '0 auto' }} />
          <h4 className="font-heading text-ink mt-4" style={{ fontSize: 20, fontWeight: 600 }}>Готово</h4>
          <p className="text-muted mt-2" style={{ fontSize: 13 }}>
            Импортировано {selected.size} ответов. Куратор получит уведомления по эл. почте.
          </p>
        </div>
      )}
    </Modal>
  );
}

function StepIndicator({ current }: { current: Step }) {
  return (
    <div className="flex items-center" style={{ gap: 12 }}>
      {[1, 2, 3].map((n) => {
        const done = current > n;
        const active = current === n;
        return (
          <div key={n} className="flex items-center" style={{ gap: 12, flex: n < 3 ? 1 : 0 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <span
                className="rounded-pill flex items-center justify-center tabular"
                style={{
                  width: 28, height: 28,
                  background: done ? '#10B981' : active ? '#F59E0B' : '#F3F4F6',
                  color: (done || active) ? '#FFFFFF' : '#6B7280',
                  fontWeight: 600, fontSize: 13,
                }}
              >{done ? <Check size={14} stroke="#FFFFFF" /> : n}</span>
              <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#0A1628' : '#6B7280' }}>
                {n === 1 ? 'Файл' : n === 2 ? 'Просмотр' : 'Готово'}
              </span>
            </div>
            {n < 3 && <span style={{ flex: 1, height: 1, background: '#E5E7EB' }} />}
          </div>
        );
      })}
    </div>
  );
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}
